import { recommendationToPrescriptionProduct } from "./inspectionRecommendation";

const cultureIdOf = (diagnosis) =>
  diagnosis?.culturaSelecionada === null ||
  diagnosis?.culturaSelecionada === undefined
    ? ""
    : String(diagnosis.culturaSelecionada);

export const inspectionPrescriptionCultureOptions = (inspection) => {
  const cultures = new Map();

  (inspection?.diagnosticos || []).forEach((diagnosis) => {
    const id = cultureIdOf(diagnosis);
    if (!id) return;

    const current = cultures.get(id) || {
      id,
      name: diagnosis.cultureName || "Sem cultura",
      diagnosisCount: 0,
      stockProductCount: 0,
      compendiumProductCount: 0,
      recipeIds: [],
      hasRecipe: false,
    };
    const products = diagnosis.recommendedProducts || [];
    current.diagnosisCount += 1;
    current.stockProductCount += products.filter(
      (product) => product.source === "stock" && product.stockProductId
    ).length;
    current.compendiumProductCount += products.filter(
      (product) => product.source === "compendium"
    ).length;
    if (diagnosis.recipeId && !current.recipeIds.includes(diagnosis.recipeId)) {
      current.recipeIds.push(diagnosis.recipeId);
    }
    current.hasRecipe = current.recipeIds.length > 0;
    cultures.set(id, current);
  });

  return [...cultures.values()];
};

export const buildPrescriptionDraftFromInspection = ({
  inspection,
  cultureId,
  responsible,
}) => {
  const selectedCultureId = String(cultureId || "");
  const diagnoses = (inspection?.diagnosticos || []).filter(
    (diagnosis) => cultureIdOf(diagnosis) === selectedCultureId
  );
  if (!inspection?.id || !diagnoses.length) return null;

  const recommendationsById = new Map();
  diagnoses.forEach((diagnosis) => {
    (diagnosis.recommendedProducts || []).forEach((product) => {
      recommendationsById.set(
        product.id || `${diagnosis.id}:${product.stockProductId}:${product.targetId}`,
        product
      );
    });
  });
  const recommendations = [...recommendationsById.values()];
  const pendingInspectionProducts = recommendations
    .filter((product) => product.source === "stock" && product.stockProductId)
    .map(recommendationToPrescriptionProduct);

  return {
    unavailableProductCount:
      recommendations.length - pendingInspectionProducts.length,
    recommendationCount: recommendations.length,
    draft: {
      idCliente: inspection.idCliente,
      idFuncionario: inspection.idFuncionario || null,
      idResponsavelTecnico: inspection.idResponsavelTecnico,
      idPropriedade: inspection.idTalhao || null,
      idInspecao: inspection.id,
      tipo: "Receita",
      saleType: "Venda",
      status: "Rascunho",
      art: responsible?.art || null,
      receita: responsible?.receita || 0,
      complementoReceita: responsible?.complementoReceita || null,
      serieSC: responsible?.serieSC || 0,
      dadosAdicionais: diagnoses
        .map((diagnosis) => diagnosis.recomendacaoManejo)
        .filter(Boolean)
        .join("\n"),
      inspectionDiagnosisIds: diagnoses.map((diagnosis) => diagnosis.id),
      inspectionCultureId: diagnoses[0].culturaSelecionada,
      pendingInspectionProducts,
    },
  };
};

export const extractInspectionPrescriptionOrigin = (saleData, routeOrigin) => {
  const inspectionId = saleData.idInspecao || saleData.inspectionId;
  const isInspectionOrigin =
    routeOrigin === "inspecao" &&
    inspectionId &&
    Array.isArray(saleData.inspectionDiagnosisIds);

  return {
    pendingProducts: isInspectionOrigin
      ? (saleData.pendingInspectionProducts || []).map((product) => ({
          ...product,
          infortecnicaReceita: product.infortecnicaReceita
            ? { ...product.infortecnicaReceita }
            : product.infortecnicaReceita,
        }))
      : [],
    origin: isInspectionOrigin
      ? {
          inspectionId,
          diagnosisIds: saleData.inspectionDiagnosisIds || [],
          cultureId: saleData.inspectionCultureId,
        }
      : null,
  };
};

export const removeInspectionPrescriptionTransientFields = (saleData) => {
  Reflect.deleteProperty(saleData, "pendingInspectionProducts");
  Reflect.deleteProperty(saleData, "inspectionId");
  Reflect.deleteProperty(saleData, "inspectionDiagnosisId");
  Reflect.deleteProperty(saleData, "inspectionDiagnosisIds");
  Reflect.deleteProperty(saleData, "inspectionCultureId");
  return saleData;
};

export const inspectionPrescriptionMissingFields = (sale, config) => {
  const requiredFields = [
    ["data", sale?.data],
    ["cliente", sale?.idCliente],
    ["propriedade", sale?.idPropriedade],
    ["responsável técnico", sale?.idResponsavelTecnico],
    ["ART/TRT", sale?.art],
    ["número da receita", Number(sale?.receita) > 0],
  ];

  if (config?.receita?.informationFiscalRequired) {
    requiredFields.push(
      ["número da NF-e", Number(sale?.numeroNFe) > 0],
      ["série da NF-e", Number(sale?.serieNFe) > 0]
    );
  }

  return requiredFields
    .filter(([, value]) => !value)
    .map(([label]) => label);
};

export const inspectionProductNeedsReview = (product) =>
  !(
    Number(product?.quantidade) > 0 &&
    Number(product?.infortecnicaReceita?.areatratar) > 0 &&
    Number(product?.infortecnicaReceita?.numAplicacoes) > 0
  );
